<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import { computed, onMounted, nextTick } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'
import { useScrollReveal } from '@/composables/useScrollReveal.js'

const props = defineProps({
  landing: { type: Object, required: true },
  topic: { type: String, required: true },
  canonicalPath: { type: String, required: true },
  alternateLocales: { type: Array, default: () => [] },
  structuredData: { type: [Object, Array], default: null },
})

const { get, locale, webRoute } = useWebTranslations()
const { refresh } = useScrollReveal()

const adminUrl = 'https://admin.growiyo.com'
const pricingHref = computed(() => webRoute('web.pricing'))
const homeHref = computed(() => webRoute('web.home'))

const ui = computed(() => {
  const map = {
    en: {
      ctaPrimary: 'Explore Growiyo',
      ctaSecondary: 'Read more',
      faq: 'Frequently asked questions',
      micro: ['Hosting included', 'No coding required', 'BackOffice included'],
      mockTitle: 'Your business. One system.',
      mockBody: 'Website, sales and everyday business administration in one place.',
      customers: 'Customers',
      quotes: 'Quotes',
      documents: 'Documents',
    },
    sl: {
      ctaPrimary: 'Raziščite Growiyo',
      ctaSecondary: 'Preberite več',
      faq: 'Pogosta vprašanja',
      micro: ['Gostovanje vključeno', 'Brez programiranja', 'BackOffice vključen'],
      mockTitle: 'Vaše podjetje. En sistem.',
      mockBody: 'Spletna stran, prodaja in poslovna administracija na enem mestu.',
      customers: 'Stranke',
      quotes: 'Ponudbe',
      documents: 'Dokumenti',
    },
    hr: {
      ctaPrimary: 'Istražite Growiyo',
      ctaSecondary: 'Pročitajte više',
      faq: 'Često postavljana pitanja',
      micro: ['Hosting uključen', 'Bez programiranja', 'BackOffice uključen'],
      mockTitle: 'Vaše poslovanje. Jedan sustav.',
      mockBody: 'Web stranica, prodaja i svakodnevna poslovna administracija na jednom mjestu.',
      customers: 'Klijenti',
      quotes: 'Ponude',
      documents: 'Dokumenti',
    },
    sr: {
      ctaPrimary: 'Istražite Growiyo',
      ctaSecondary: 'Pročitajte više',
      faq: 'Često postavljana pitanja',
      micro: ['Hosting uključen', 'Bez programiranja', 'BackOffice uključen'],
      mockTitle: 'Vaše poslovanje. Jedan sistem.',
      mockBody: 'Veb sajt, prodaja i svakodnevna poslovna administracija na jednom mestu.',
      customers: 'Klijenti',
      quotes: 'Ponude',
      documents: 'Dokumenti',
    },
    bs: {
      ctaPrimary: 'Istražite Growiyo',
      ctaSecondary: 'Pročitajte više',
      faq: 'Često postavljana pitanja',
      micro: ['Hosting uključen', 'Bez programiranja', 'BackOffice uključen'],
      mockTitle: 'Vaše poslovanje. Jedan sistem.',
      mockBody: 'Web stranica, prodaja i svakodnevna poslovna administracija na jednom mjestu.',
      customers: 'Klijenti',
      quotes: 'Ponude',
      documents: 'Dokumenti',
    },
  }
  return map[locale.value] ?? map.en
})

const ctaPrimary = computed(() => ui.value.ctaPrimary)
const ctaSecondary = computed(() => ui.value.ctaSecondary)
const faqEyebrow = computed(() => ui.value.faq)
const faqHeading = computed(() => ui.value.faq)

const micro = computed(() =>
  Array.isArray(props.landing.micro) && props.landing.micro.length
    ? props.landing.micro
    : ui.value.micro
)

const sections = computed(() =>
  Array.isArray(props.landing.sections) ? props.landing.sections : []
)

const faq = computed(() =>
  Array.isArray(props.landing.faq) ? props.landing.faq : []
)

const callout = computed(() => props.landing.callout ?? null)

const isSplitHero = computed(() => props.landing.variant === 'c')

onMounted(async () => {
  await nextTick()
  refresh()
})
</script>

<template>
  <SEOHead
    :title="landing.seo_title"
    :description="landing.seo_description"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="alternateLocales"
    :structured-data="structuredData"
  />

  <!-- Hero -->
  <section class="gw-paper border-b gw-rule overflow-hidden">
    <div
      class="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24"
      :class="isSplitHero ? 'lg:grid lg:grid-cols-2 lg:gap-14 lg:items-center' : 'text-center'"
    >
      <div>
        <p
          class="inline-flex items-center gap-2 gw-mono text-[10.5px] font-medium uppercase tracking-[0.22em] text-[rgba(51,51,51,0.55)] border gw-rule-strong rounded-full px-3 py-1.5"
          data-reveal
        >
          <span class="w-1.5 h-1.5 rounded-full bg-[color:var(--gw-magenta)]" aria-hidden="true" />
          {{ landing.eyebrow }}
        </p>
        <h1
          class="gw-title mt-6 max-w-3xl"
          :class="isSplitHero ? '' : 'mx-auto'"
          data-reveal
        >
          {{ landing.h1 }}
        </h1>
        <p
          class="mt-5 text-[1.05rem] leading-relaxed text-[color:var(--gw-ink-soft)] max-w-2xl"
          :class="isSplitHero ? '' : 'mx-auto'"
          data-reveal
        >
          {{ landing.lead }}
        </p>
        <div
          class="mt-8 flex flex-wrap gap-3"
          :class="isSplitHero ? '' : 'justify-center'"
          data-reveal
        >
          <a :href="adminUrl" class="gw-btn-grad inline-flex items-center gap-2">
            {{ ctaPrimary }}
            <span aria-hidden="true">↗</span>
          </a>
          <a href="#content" class="gw-btn-ghost inline-flex items-center">
            {{ ctaSecondary }}
          </a>
        </div>
        <div
          class="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[rgba(51,51,51,0.6)]"
          :class="isSplitHero ? '' : 'justify-center'"
          data-reveal
        >
          <span
            v-for="(item, i) in micro"
            :key="i"
            class="inline-flex items-center gap-1.5"
          >
            <span class="text-[color:var(--gw-pink)] font-semibold" aria-hidden="true">✓</span>
            {{ item }}
          </span>
        </div>
      </div>

      <div
        v-if="isSplitHero"
        class="mt-12 lg:mt-0 rounded-2xl p-[1px] gw-gradient-bg shadow-[0_24px_60px_rgba(51,51,51,0.12)]"
        data-reveal
      >
        <div class="rounded-2xl bg-white overflow-hidden">
          <div class="h-11 flex items-center gap-1.5 px-4 border-b gw-rule bg-[var(--gw-paper-soft)]">
            <span class="w-2.5 h-2.5 rounded-full bg-[rgba(51,51,51,0.2)]" />
            <span class="w-2.5 h-2.5 rounded-full bg-[rgba(51,51,51,0.2)]" />
            <span class="w-2.5 h-2.5 rounded-full bg-[rgba(51,51,51,0.2)]" />
            <span class="ml-3 gw-mono text-[10px] uppercase tracking-[0.16em] text-[rgba(51,51,51,0.45)] truncate">
              yourbrand.growiyo.com
            </span>
          </div>
          <div class="grid md:grid-cols-2 min-h-[280px]">
            <div class="p-8 flex flex-col justify-center">
              <p class="gw-mono text-[10px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.45)]">Growiyo</p>
              <p class="mt-2 text-2xl font-semibold tracking-[-0.03em] text-[color:var(--gw-ink)] leading-tight">
                {{ ui.mockTitle }}
              </p>
              <p class="mt-3 text-sm text-[color:var(--gw-ink-soft)]">
                {{ ui.mockBody }}
              </p>
            </div>
            <div class="bg-[color:var(--gw-ink)] p-6 flex items-center">
              <div class="w-full rounded-xl bg-white p-4">
                <div class="flex items-center justify-between mb-4">
                  <span class="font-semibold text-sm text-[color:var(--gw-ink)]">BackOffice</span>
                  <span class="gw-mono text-[9px] uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-[var(--gw-paper)] text-[rgba(51,51,51,0.55)]">
                    Live
                  </span>
                </div>
                <div class="grid grid-cols-3 gap-2 mb-3">
                  <div class="rounded-lg bg-[var(--gw-paper)] p-2.5">
                    <p class="text-[10px] text-[rgba(51,51,51,0.5)]">{{ ui.customers }}</p>
                    <p class="text-lg font-semibold text-[color:var(--gw-ink)]">248</p>
                  </div>
                  <div class="rounded-lg bg-[var(--gw-paper)] p-2.5">
                    <p class="text-[10px] text-[rgba(51,51,51,0.5)]">{{ ui.quotes }}</p>
                    <p class="text-lg font-semibold text-[color:var(--gw-ink)]">36</p>
                  </div>
                  <div class="rounded-lg bg-[var(--gw-paper)] p-2.5">
                    <p class="text-[10px] text-[rgba(51,51,51,0.5)]">{{ ui.documents }}</p>
                    <p class="text-lg font-semibold text-[color:var(--gw-ink)]">124</p>
                  </div>
                </div>
                <div class="flex items-end gap-1.5 h-20 rounded-lg bg-[var(--gw-paper-soft)] p-2">
                  <span
                    v-for="(h, i) in [38, 62, 47, 81, 70, 95, 76]"
                    :key="i"
                    class="flex-1 rounded-t-md gw-gradient-bg opacity-90"
                    :style="{ height: h + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Content sections -->
  <section
    v-for="(section, index) in sections"
    :id="index === 0 ? 'content' : undefined"
    :key="index"
    :class="{
      'gw-paper': section.tone === 'default' || !section.tone,
      'gw-paper-soft': section.tone === 'soft',
      'gw-ink-surface': section.tone === 'dark',
      'border-t gw-rule': section.tone !== 'dark',
      'border-t border-[rgba(242,240,239,0.12)]': section.tone === 'dark',
    }"
  >
    <div class="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
      <article class="lg:col-span-7" data-reveal>
        <p
          class="gw-mono text-[10.5px] font-medium uppercase tracking-[0.22em] mb-3"
          :class="section.tone === 'dark' ? 'text-[rgba(242,240,239,0.55)]' : 'text-[rgba(51,51,51,0.5)]'"
        >
          {{ section.kicker }}
        </p>
        <h2
          class="gw-title max-w-xl"
          :class="section.tone === 'dark' ? 'gw-title--inverse' : ''"
        >
          {{ section.heading }}
        </h2>
        <div class="mt-6 space-y-4 text-[1.05rem] leading-relaxed"
             :class="section.tone === 'dark' ? 'text-[rgba(242,240,239,0.72)]' : 'text-[color:var(--gw-ink-soft)]'">
          <p v-for="(paragraph, pi) in section.paragraphs" :key="pi">
            {{ paragraph }}
          </p>
        </div>
      </article>

      <aside
        class="lg:col-span-5 lg:sticky lg:top-28 rounded-2xl p-7 border"
        :class="section.tone === 'dark'
          ? 'bg-[rgba(242,240,239,0.06)] border-[rgba(242,240,239,0.12)]'
          : 'bg-white gw-rule-strong shadow-[0_18px_40px_rgba(51,51,51,0.06)]'"
        data-reveal
      >
        <h3
          class="text-xl font-semibold tracking-[-0.02em] mb-4"
          :class="section.tone === 'dark' ? 'text-[color:var(--gw-paper)]' : 'text-[color:var(--gw-ink)]'"
        >
          {{ section.aside?.title }}
        </h3>
        <p
          v-if="section.aside?.body"
          class="text-[0.95rem] leading-relaxed mb-5"
          :class="section.tone === 'dark' ? 'text-[rgba(242,240,239,0.65)]' : 'text-[color:var(--gw-ink-soft)]'"
        >
          {{ section.aside.body }}
        </p>
        <ul v-if="section.aside?.checks?.length" class="space-y-3">
          <li
            v-for="(check, ci) in section.aside.checks"
            :key="ci"
            class="flex gap-3 items-start py-2 border-b last:border-0"
            :class="section.tone === 'dark' ? 'border-[rgba(242,240,239,0.1)]' : 'gw-rule'"
          >
            <span
              class="shrink-0 w-6 h-6 rounded-full gw-gradient-bg text-white text-xs font-bold grid place-items-center mt-0.5"
              aria-hidden="true"
            >✓</span>
            <span :class="section.tone === 'dark' ? 'text-[color:var(--gw-paper)]' : 'text-[color:var(--gw-ink)]'">
              {{ check }}
            </span>
          </li>
        </ul>
        <a
          v-if="section.aside?.cta"
          :href="adminUrl"
          class="gw-btn-grad inline-flex mt-5"
        >
          {{ ctaPrimary }}
        </a>
      </aside>
    </div>
  </section>

  <!-- Callout -->
  <section v-if="callout" class="gw-paper border-t gw-rule">
    <div class="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20" data-reveal>
      <div class="rounded-2xl p-8 lg:p-12 gw-gradient-bg text-white">
        <h2 class="text-[clamp(1.75rem,3vw,2.75rem)] font-semibold tracking-[-0.03em] leading-tight max-w-3xl">
          {{ callout.heading }}
        </h2>
        <p class="mt-4 text-[1.05rem] leading-relaxed text-white/85 max-w-2xl">
          {{ callout.body }}
        </p>
        <div class="mt-8 flex flex-wrap gap-3">
          <a :href="adminUrl" class="inline-flex items-center gap-2 rounded-full bg-white text-[color:var(--gw-ink)] font-semibold px-5 py-3 hover:opacity-95 transition">
            {{ ctaPrimary }}
            <span aria-hidden="true">↗</span>
          </a>
          <a :href="pricingHref" class="inline-flex items-center rounded-full border border-white/40 text-white font-semibold px-5 py-3 hover:bg-white/10 transition">
            {{ get('layout.nav.pricing') }}
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- FAQ -->
  <section v-if="faq.length" class="gw-paper-soft border-t gw-rule">
    <div class="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div class="mb-10" data-reveal>
        <p class="gw-mono text-[10.5px] font-medium uppercase tracking-[0.22em] text-[rgba(51,51,51,0.5)] mb-3">
          {{ faqEyebrow }}
        </p>
        <h2 class="gw-title">{{ faqHeading }}</h2>
      </div>
      <div class="divide-y gw-rule-strong border-t gw-rule-strong" data-reveal>
        <details
          v-for="(item, i) in faq"
          :key="i"
          class="group py-5"
        >
          <summary class="cursor-pointer list-none flex items-start justify-between gap-4 text-[1.05rem] font-semibold text-[color:var(--gw-ink)] tracking-[-0.01em]">
            <span>{{ item.q }}</span>
            <span class="shrink-0 text-[color:var(--gw-pink)] group-open:rotate-45 transition-transform text-xl leading-none" aria-hidden="true">+</span>
          </summary>
          <p class="mt-3 text-[0.98rem] leading-relaxed text-[color:var(--gw-ink-soft)] pr-8">
            {{ item.a }}
          </p>
        </details>
      </div>
      <p class="mt-10 text-sm text-[rgba(51,51,51,0.5)]" data-reveal>
        <a :href="homeHref" class="underline underline-offset-2 hover:text-[color:var(--gw-ink)]">
          Growiyo
        </a>
      </p>
    </div>
  </section>
</template>
